// src/hooks/queries/use-user.ts
"use client";

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "./query-keys";

export type UserMeta = {
  percentage?: number;
  minWithdrawFiat?: number;
  twoFactorEnabled?: boolean;
  /** Set when we cleared their enrolment for them; drives the setup-page notice. */
  twoFactorResetAt?: string | null;
  kycStatus?: string;
  kycRequired?: boolean;
  kycEnabled?: boolean;
  kyrrexCardEnabled?: boolean;
  /** Card products this merchant may issue. Subset of {"standard","atm"}. */
  offRampCardKinds?: ("standard" | "atm")[];
  /** Staff grant for the GlacierPay banking rail. See /api/banking. */
  bankingRailEnabled?: boolean;
  /** "owner" acting on their own account, or "staff" acting for a merchant. */
  userType?: string;
  /** The owner `user.id` this account acts for (self, when `userType` is "owner"). */
  merchantId?: string;
  /** ISO 3166-1 alpha-2 country on the account, when set. */
  country?: string | null;
  /** Whether the account's email has been verified with Better Auth. */
  emailVerified?: boolean | null;
  /** The `$set_once` `signup_method` person property: `"email"` for a direct sign-up, or
   *  `null` when it cannot be sourced (bridge/partner-provisioned account,
   *  or a staff caller — see route.ts). */
  signupMethod?: string | null;
  /** `merchantOnboardingApplication.status`, or null when no application exists yet. */
  onboardingStatus?: string | null;
};

export function useUserMetaQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.user.meta(),
    queryFn: () => apiFetch<UserMeta>("/api/user/meta"),
    ...options,
  });
}
